import { useState, useEffect, useRef, useCallback } from "react";

export const AUTO_REFRESH_MS = 10000;

// Keeps a screen current without a page reload. `refresh` is the screen's own fetch (async, errors are swallowed).
// It runs on an interval while the browser tab is visible, again when the tab or window regains focus, and on demand
// through the returned `refresh` (the refresh button); `refresh` receives `true` for that manual run. Runs never overlap. While `paused` is true (a dialog or a form
// is open) the automatic runs wait so the screen does not change under the user; the manual refresh still works.
export function useAutoRefresh(refresh, { interval = AUTO_REFRESH_MS, paused = false } = {}) {
  const refreshRef = useRef(refresh);
  const busyRef = useRef(false);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => { refreshRef.current = refresh; });

  const run = useCallback(async (manual) => {
    if (busyRef.current) return;
    busyRef.current = true;
    if (manual) setRefreshing(true);
    try { await refreshRef.current(manual); } catch { /* keep the data already on screen */ }
    busyRef.current = false;
    if (manual) setRefreshing(false);
  }, []);

  useEffect(() => {
    if (paused) return undefined;
    const tick = () => { if (document.visibilityState === "visible") run(false); };
    const timer = setInterval(tick, interval);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("focus", tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
      window.removeEventListener("focus", tick);
    };
  }, [interval, paused, run]);

  const manualRefresh = useCallback(() => run(true), [run]);
  return { refresh: manualRefresh, refreshing };
}
